import {Button, Checkbox, Col, DatePicker, Layout, message, Row, Space, Spin, Typography} from 'antd';
import { InputNumber, Select, Table } from 'antd';
import React, { useEffect, useState, useContext, useCallback, useMemo, ReactNode } from 'react';
import { isEmpty, keys } from 'underscore';
import { Label } from '../../../../components/Label/Label';
import guid from '../../../../generic/Guid';
import { IPlotVariable } from '../../../../typings/analytics';
import styles from '../../Style/LayoutPanel.module.scss';
import {  ChartBox } from '../ChartBox';
import { advancedAnalyticsMonthOptions } from './MonthOptions';
import { IBestDataset } from '../../../../typings/analytics';
import { IEnrichedMetadata } from '../../../../typings/metadata';
import { IScatterTableApiPayload } from '../../../../typings/chart';
import { DateFormat, DT0Format } from '../../../../generic/StaticVariable';
import moment, { Moment } from 'moment';
import { IAnalytcisUserInput } from '../../../../typings/IAnalyticsUserInput';
import Api from '../../../../api/Api';
import { StoreContext } from '../../../../store/store';
import IconPoint from '../../../../assets/icon/locate2.png'
import CustomDivider from '../CustomDIvider';
import { MarkerItem } from '../../../../typings/home';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

export default function ScatterTable(props: {
  coordinates: number[];
  previewDatasetId: string;
  categoryDataset: IBestDataset[];
  onDatasetChange: (item: string) => void
  enrichedMetadata: IEnrichedMetadata[];
  onUpdate: (payload: IScatterTableApiPayload) => void;
  error: string;
  data: any[];
  dateRange: [moment.Moment, moment.Moment];
  scatterTableLinks: string[];
  pointName: string;
  userInput: IAnalytcisUserInput;
  gridCellSize: string,
  waterDepth: string,
  listNavigation: ReactNode
  updateUserInput: React.Dispatch<Partial<IAnalytcisUserInput>>;
  point?: MarkerItem
  onChangeActiveKey: (item: any) => void
  pointOption: { label: string, value: string }[]
  globalDateRange: any[];
  setGlobalDateRange: (item: any) => void;
  isForAllAnalytics: boolean
  setIsForAllAnalytics: (item: any) => void
  updating: boolean
}) {
  const {
    onUpdate,
    updating,
    dateRange,
    previewDatasetId,
    coordinates,
    enrichedMetadata,
    error,
    data,
    scatterTableLinks,
    pointName,
    userInput,
    updateUserInput,
    categoryDataset,
    onDatasetChange,
    gridCellSize,
    waterDepth,
    listNavigation,
    point,
    onChangeActiveKey,
    pointOption,
    globalDateRange,
    setGlobalDateRange,
    isForAllAnalytics,
    setIsForAllAnalytics,
  } = props;

  const context = useContext(StoreContext)

  const [dataset, setDataset] = useState(userInput.ScatterTable.datasetX);
  const [datasetY, setDatasetY] = useState(userInput.ScatterTable.datasetY);
  const [variableX, setVariableX] = useState<IPlotVariable | null>(
    userInput.ScatterTable.variableX
  );
  const [variableY, setVariableY] = useState<IPlotVariable | null>(
    userInput.ScatterTable.variableY
  );
  const [tempVariableX, setTempVariableX] = useState<IPlotVariable | null>(
    userInput.ScatterTable.variableX
  );
  const [tempVariableY, setTempVariableY] = useState<IPlotVariable | null>(
    userInput.ScatterTable.variableY
  );
  const [month, setMonth] = useState(userInput.ScatterTable.month);
  const [intervalX, setIntervalX] = useState(userInput.ScatterTable.intervalX);
  const [intervalY, setIntervalY] = useState(userInput.ScatterTable.intervalY);
  const [tableData, setTableData] = useState<any[]>([]);
  const [column, setColumn] = useState<any[]>([]);
  const [variablesX, setVariablesX] = useState<IPlotVariable[]>([]);
  const [variablesY, setVariablesY] = useState<IPlotVariable[]>([]);
  const [isChangeDatasetY, setIsChangeDatasetY] = useState(false)
  const [startDate, setStartDate] = useState(
    userInput.ScatterTable.startDate || moment(dateRange[0])
  );
  const [endDate, setEndDate] = useState(
    userInput.ScatterTable.endDate || moment(dateRange[1])
  );

  useEffect(() => {
    if(isForAllAnalytics){
      setStartDate(globalDateRange[0])
      setEndDate(globalDateRange[1])
    }
  }, [isForAllAnalytics])

  useEffect(() => {
    updateUserInput({
      ScatterTable: {
        datasetX: dataset,
        datasetY: datasetY,
        variableX: variableX,
        variableY: variableY,
        startDate: startDate,
        endDate: endDate,
        month: month,
        intervalX: intervalX,
        intervalY: intervalY,
      },
    });
  }, [
    dataset,
    datasetY,
    variableX,
    variableY,
    startDate,
    endDate,
    month,
    intervalX,
    intervalY,
  ]);

  const getData = useCallback(
    (
      dataset1: string,
      dataset2: string,
      indexX: IPlotVariable,
      indexY: IPlotVariable,
      orderId: string,
      orderId2: string,
      requestId: string,
      requestId2: string,
      startDate: string,
      endDate: string
    ) => {
      const [longitude, latitude] = coordinates;
      if (indexX && indexY) {
        const payload: IScatterTableApiPayload = {
          AcronymX: indexX.ShortName ? indexX.ShortName : indexX.Name,
          AcronymY: indexY.ShortName ? indexY.ShortName : indexY.Name,
          ColumnIndexX: indexX.Index,
          ColumnIndexY: indexY.Index,
          DatasetId: dataset1,
          DatasetId2: dataset2,
          EndDate: endDate,
          StartDate: startDate,
          IntervalCountX: intervalX,
          IntervalCountY: intervalY,
          Longitude: longitude.toString(),
          Latitude: latitude.toString(),
          Month: month,
          OrderId: orderId,
          OrderId2: orderId2,
          OriginalDatasetId: dataset1,
          OriginalOrderId: orderId,
          TitleX: indexX.Name,
          PlotFlag: '[3]',
          TitleY: indexY.Name,
          RequestId: requestId,
          RequestId2: requestId2,
        };
        onUpdate(payload);
      }
    },
    [coordinates, onUpdate, intervalX, intervalY, month, startDate, endDate]
  );

  useEffect(() => {
    if (previewDatasetId && !isEmpty(categoryDataset) && !isEmpty(coordinates)) {
      setDataset(previewDatasetId);
      setDatasetY(previewDatasetId);
      if (enrichedMetadata) {
        const task = enrichedMetadata.find(
          (task: IEnrichedMetadata) =>
            task.DatasetId === previewDatasetId &&
            task.CoordinatesString === coordinates.join()
        );
        if (task) {
          const defaultItems = task.ScatterTableDefaultItems.split(',');
          const itemsInfos = task.ItemsInfo.filter(
            (itemInfo: IPlotVariable) => itemInfo.Name !== 'Time'
          );
          if (itemsInfos.length > 1) {
            setVariablesX(itemsInfos);
            setVariablesY(itemsInfos);
            let indexX = itemsInfos[0];
            let indexY = itemsInfos[1];
            if (defaultItems.length > 1) {
              const defaultItem1 = task.ItemsInfo.find(
                (itemInfo: IPlotVariable) =>
                  itemInfo.ShortName === defaultItems[0]
              );
              const defaultItem2 = task.ItemsInfo.find(
                (itemInfo: IPlotVariable) =>
                  itemInfo.ShortName === defaultItems[1]
              );
              if (defaultItem1 !== undefined) {
                indexX = defaultItem1;
              }
              if (defaultItem2 !== undefined) {
                indexY = defaultItem2;
              }
            }
            setTempVariableX(indexX);
            setTempVariableY(indexY);
            setVariableX(indexX)
            setVariableY(indexY)
            let dateRange = [startDate, endDate];
            if(isForAllAnalytics){
              dateRange = globalDateRange
              setStartDate(dateRange[0])
              setEndDate(dateRange[1])
            }

            getData(
              previewDatasetId,
              previewDatasetId,
              indexX,
              indexY,
              task.OrderId,
              task.OrderId,
              task.RequestId,
              task.RequestId,
              dateRange[0].format(DT0Format),
              dateRange[1].format(DT0Format)
            );
          }
        }
      }
    }
  }, [previewDatasetId, categoryDataset, enrichedMetadata, coordinates]);

  const onUpdateData = useCallback(() => {
    if (dataset && datasetY && enrichedMetadata && tempVariableX && tempVariableY) {
      const task = enrichedMetadata.find(
        (task: IEnrichedMetadata) =>
          task.DatasetId === dataset &&
          task.CoordinatesString === coordinates.join()
      );
      if (task) {
        let orderId2 = task.OrderId;
        let requestId2 = task.RequestId;
        if (dataset !== datasetY) {
          const task2 = context.enrichedMetadata.find(
            (task: IEnrichedMetadata) =>
              task.DatasetId === datasetY &&
              task.CoordinatesString === coordinates.join()
          );
          if (task2) {
            orderId2 = task2.OrderId;
            requestId2 = task2.RequestId;
          }
        }
        setVariableX(tempVariableX)
        setVariableY(tempVariableY)
        getData(
          dataset,
          datasetY,
          tempVariableX,
          tempVariableY,
          task.OrderId,
          orderId2,
          task.RequestId,
          requestId2,
          startDate.format(DT0Format),
          endDate.format(DT0Format)
        );
      }
    }
  }, [
    enrichedMetadata,
    tempVariableX,
    tempVariableY,
    dataset,
    datasetY,
    intervalX,
    intervalY,
    startDate,
    endDate,
    month,
  ]);

  useEffect(() => {
    if (isEmpty(data)) {
      return;
    }
    const tempColumn: any[] = [];
    keys(data[0]).forEach((key) => {
      tempColumn.push({
        title: key,
        dataIndex: key,
        width: 80,
        // key: 'id',
      });
    });
    const tempData = data.map((item) => {
      item.key = guid();
      return item;
    });

    setColumn(tempColumn);

    setTableData(tempData);
  }, [data]);

  const doMonthChange = (selectedMonth: string) => {
    setMonth(selectedMonth);
  };

  const doVariableChangeX = (active: number) => {
    const item = variablesX.find((a) => a.Index === active)!;
    setTempVariableX(item);
  };

  const doVariableChangeY = (active: number) => {
    const item = variablesY.find((a) => a.Index === active)!;
    setTempVariableY(item);
  };

  const doChangeIntervalX = (active: number | string | null | undefined) => {
    if (active !== undefined && !isNaN(Number(active))) {
      setIntervalX(Number(active));
    }
  };

  const doChangeIntervalY = (active: number | string | null | undefined) => {
    if (active !== undefined && !isNaN(Number(active))) {
      setIntervalY(Number(active));
    }
  };

  const doDatasetYChange = async (active: string) => {
    if (!enrichedMetadata) return;

    let metadata = enrichedMetadata.find(
      (md: IEnrichedMetadata) =>
        md.DatasetId === active &&
        md.CoordinatesString === coordinates.join()
    );

    if (!metadata) {
      try {
        setIsChangeDatasetY(true)
        metadata = await Api.metadata.getMetadata(active);
        const createFullExtractionTasks = await Api.service.createFullExtractionTasks(
          [metadata!],
          coordinates,
          pointName,
          gridCellSize,
          waterDepth,
          context.datasetBriefs,
          context.fwePoints,
        );
        const addEnrichedMetadata = enrichedMetadata.concat(createFullExtractionTasks)
        context.setEnrichedMetadata(addEnrichedMetadata);
      } catch (err) {
        console.log(err)
      } finally {
        setIsChangeDatasetY(false)
      }
    }

    if (!metadata) return

    const withoutTime = metadata.ItemsInfo.filter(
      (item: IPlotVariable) => item.Name !== 'Time'
    );
    setVariablesY(withoutTime);
    setTempVariableY(withoutTime.length > 0 ? withoutTime[0] : null);
    setDatasetY(active);

  };

  const doStartDateChange = (value: any) => {
    if (value) {
      setStartDate(value);
      if(isForAllAnalytics){
        setGlobalDateRange([value, endDate])
      }
    }
  };

  const doEndDateChange = (value: any) => {
    if (value) {
      setEndDate(value);
      if(isForAllAnalytics){
        setGlobalDateRange([startDate, value])
      }
    }
  };

  const onselectDatasetX = (value: string) => {
    onDatasetChange(value)
  }

  function disabledStartDate(current: Moment) {
    // Can not select date after end date
    const isAfterEndDate = current && current > endDate;
    // and not before preview dataset's time range and not after preview dataset's time range
    return isAfterEndDate
      ? isAfterEndDate
      : current.isBefore(dateRange[0], 'day');
  }

  function disabledEndDate(current: Moment) {
    // Can not select date before start date and not after preview dataset's time range
    const isBeforeStartDate = current && current < startDate;
    return isBeforeStartDate
      ? isBeforeStartDate
      : current.isAfter(dateRange[1], 'day');
  }

  const categories = useMemo(() => {
    const label = categoryDataset.map((bd: IBestDataset) => {
      return bd.label;
    })

    return Array.from(new Set(label));
  }, [categoryDataset])

  const handleCheckboxChange = () => {
    setGlobalDateRange([startDate, endDate])
    setIsForAllAnalytics(!isForAllAnalytics);
  };

  return (
    <Layout style={{minWidth: 1200, height: '100%', overflow: 'hidden', background: 'white'}}>
      <Header className={styles.header}>
        <Row justify="space-between" align="middle" className={styles.headerRow}>
          <Col>
            <Row gutter={[0, 8]} align="bottom">
              <Col className={styles.colPoint}>
              <Space>
                <Text>Point</Text>
                <img src={IconPoint} width={20} height={20} />
              </Space>
              <Select
                style={{ width: '100%', marginTop: '4px' }}
                placeholder="Select point"
                onChange={onChangeActiveKey}
                options={pointOption}
                value={point}
              />
              </Col>
              <Col span={24} md={4} className={styles.colItem}>
                <Text>Dataset</Text>
                <br />
                <Select
                  value={dataset}
                  style={{ width: '100%', marginTop: '4px' }}
                  onChange={onselectDatasetX}
                >
                  {categories.map((category: string) => {
                    const section = category
                    return (
                      <Select.OptGroup key={category} label={section}>
                        {categoryDataset
                          .filter((item) => {
                            return item.label === category;
                          })
                          .map((item) => {
                            return (
                              <Select.Option key={item.id} value={item.id}>
                                {item.value}
                              </Select.Option>
                            );
                          })}
                      </Select.OptGroup>
                    );
                  })}
                  </Select>
              </Col>
              <Col span={24} md={4} className={styles.colItem}>
                <Text>Variable</Text>
                <br />
                <Select
                  style={{ width: '100%', marginTop: '4px'}}
                  placeholder="Select variable"
                  onChange={doVariableChangeX}
                  value={tempVariableX?.Index ? tempVariableX.Index : 0}
                  disabled={updating}
                >
                  {variablesX
                    .filter((item) => item.Name !== 'Time')
                    .map((item) => {
                      return (
                        <Select.Option key={item.Index} value={item.Index}>
                          {item.Name}
                        </Select.Option>
                      );
                    })}
                </Select>
              </Col>
              <Col span={24} md={3} className={styles.colItem}>
                <Text>Start Date</Text>
                <DatePicker
                  value={moment(startDate)}
                  disabledDate={disabledStartDate}
                  onChange={doStartDateChange}
                  format={DateFormat}
                  disabled={updating}
                  className={styles.datePicker}
                />
              </Col>
              <Col span={24} md={3} className={styles.colItem}>
                <Text>End Date</Text>
                <DatePicker
                  value={moment(endDate)}
                  disabledDate={disabledEndDate}
                  onChange={doEndDateChange}
                  format={DateFormat}
                  disabled={updating}
                  className={styles.datePicker}
                />
              </Col>
              <Col span={24} md={5} className={styles.checkboxItem} >
                <Checkbox checked={isForAllAnalytics} onClick={handleCheckboxChange} />
                <Text onClick={handleCheckboxChange} style={{cursor: 'pointer'}}>Use this period for all analytics</Text>
              </Col>
            </Row>
          </Col>
          <Col style={{width: 201}}>
          <div style={{height: '60px', padding: 20, borderLeft: '2px solid #f0f0f0'}}>
            <Button
              type="primary"
              onClick={onUpdateData}
              loading={updating}
              disabled={updating}
              style={{ width: 100, bottom: 10}}
            >
              Update
            </Button>
          </div>
          </Col>
        </Row>
      </Header>
      <CustomDivider  />
      <Layout>
        <Sider theme="light">
          {listNavigation}
        </Sider>
        <Content style={{ padding: '10px', minHeight: '400px', borderLeft: '2px solid #f0f0f0', borderRight: '2px solid #f0f0f0', background: 'white'}}>
          {!updating && error && <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>{error}</div>}
          {!updating && !error && (
          <ChartBox
            actionRow={<></>}
            key={'Scatter Table'}
            title={'Scatter Table'}
            height={updating ? '50px' : 'auto'}
            plot={
              <Table
                columns={column}
                dataSource={tableData}
                scroll={{ x: 'fit-content', y: 330 }}
                pagination={false}
                size="small"
              />
            }
            dataset={''}
            showAxis={true}
            xAxis={variableX ? variableX : undefined}
            yAxis={variableY ? variableY : undefined}
            link={scatterTableLinks}
          />
          )}
          {updating && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
              <Spin style={{margin: 'auto'}} size='large' />
            </div>
          )}
        </Content>
        <Sider
          width={200}
          className={styles.siderChildren}
          theme="light"
          style={{
            display: 'flex',
            flexDirection: 'column',
            padding: 5,
          }}
        >
          <Label label="DatasetY" className={styles.actionItem}>
            <Select
              value={datasetY}
              size="small"
              style={{ width: '100%' }}
              onChange={doDatasetYChange}
              disabled={isChangeDatasetY || updating}
            >
              {categories.map((category: string) => {
                const section = category
                return (
                  <Select.OptGroup key={category} label={section}>
                    {categoryDataset
                      .filter((item) => {
                        return item.label === category;
                      })
                      .map((item) => {
                        return (
                          <Select.Option key={item.id} value={item.id}>
                            {item.value}
                          </Select.Option>
                        );
                      })}
                  </Select.OptGroup>
                );
              })}
            </Select>
          </Label>
          <Label label="VariableY" className={styles.actionItem}>
            <Select
              value={tempVariableY?.Index ? tempVariableY.Index : 0}
              size="small"
              style={{ width: '100%' }}
              onChange={doVariableChangeY}
              disabled={updating}
            >
              {variablesY
                .filter((item) => item.Name !== 'Time')
                .map((item) => {
                  return (
                    <Select.Option key={item.Index} value={item.Index}>
                      {item.Name}
                    </Select.Option>
                  );
                })}
            </Select>
          </Label>
          <Label label="Number of Intervals X" className={styles.actionItem}>
            <InputNumber
              size="small"
              min={1}
              max={40}
              value={intervalX}
              style={{ width: '100%' }}
              onChange={doChangeIntervalX}
              disabled={updating}
            />
          </Label>
          <Label label="Number of Intervals Y" className={styles.actionItem}>
            <InputNumber
              size="small"
              min={1}
              max={40}
              style={{ width: '100%' }}
              onChange={doChangeIntervalY}
              value={intervalY}
              disabled={updating}
            />
          </Label>
          <Label label="Month" className={styles.actionItem}>
            <Select
              value={month}
              style={{ width: '100%' }}
              onChange={doMonthChange}
              options={advancedAnalyticsMonthOptions}
              disabled={updating}
            />
          </Label>
        </Sider>
      </Layout>
    </Layout>
  );
}
